<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import {
  SatSelect,
  AtomText,
  AtomLoadingSpinner,
} from "@chatfood/design-system";
import { onUnmounted, watch } from "vue";
import type { ISchedulePickerProps } from "./props";
import type { ISchedulePickerEmits } from "./emits";
import { useSchedulePicker } from "./use-schedule-picker";
import { useLocale } from "~/composables/use-locale";
import { useWindowResize } from "~/composables/use-window-resize";

const css = bemBuilder("schedule-picker");
const props = defineProps<ISchedulePickerProps>();
const emits = defineEmits<ISchedulePickerEmits>();
const { isMobile } = useWindowResize();

const { t } = useLocale();

const {
  fetchAvailableSchedules,
  isLoading,
  selectedDay,
  selectedTimeSlot,
  availableDays,
  availableTimeSlots,
  onSelectDay,
  onSelectTimeSlot,
} = useSchedulePicker(props, emits);

watch(
  () => [props.outletId, props.orderingMode],
  () => {
    fetchAvailableSchedules(props.outletId, props.orderingMode);
  },
  { immediate: true },
);

onUnmounted(() => {
  emits("unMounted");
});
</script>

<template>
  <div
    :class="{
      [css()]: true,
      [css('', 'loading')]: isLoading,
    }"
  >
    <AtomLoadingSpinner v-if="isLoading" :class="css()" :size="30" />
    <template v-else>
      <AtomText v-if="props.showLabel" type="body-bold" tag="div">
        {{ t("components.schedule_picker.label") }}
      </AtomText>
      <AtomText
        v-if="!availableDays.length"
        type="body-bold"
        tag="div"
        color="color-coral"
      >
        {{ t("components.schedule_picker.no_slots_message") }}
      </AtomText>
      <div v-else :class="css('options')">
        <SatSelect
          :class="css('date')"
          :has-errors="false"
          :placeholder="t('components.schedule_picker.select_date_placeholder')"
          :clearable="false"
          :searchable="!isMobile"
          :disabled="false"
          :append-to-body="true"
          :value="selectedDay"
          :options="availableDays"
          @select="onSelectDay"
        />
        <SatSelect
          :class="css('time')"
          :has-errors="false"
          :placeholder="t('components.schedule_picker.select_time_placeholder')"
          :clearable="false"
          :searchable="!isMobile"
          :disabled="false"
          :append-to-body="true"
          :value="selectedTimeSlot"
          :options="availableTimeSlots"
          @select="onSelectTimeSlot"
        />
      </div>
    </template>
  </div>
</template>

<style lang="scss">
.schedule-picker {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);

  &--loading {
    align-items: center;
    justify-content: center;
  }

  &__options {
    display: flex;
    gap: var(--spacing-sm);
    flex-direction: row;
  }

  &__date {
    width: 100%;
  }

  &__time {
    width: 100%;
    min-width: 185px;
  }
}
</style>
